'use client';

import React from 'react';
import { DateTime } from 'luxon';
import {
  PiArrowRight, PiMagnifyingGlass, PiMapPin, PiWhatsappLogo,
} from 'react-icons/pi';
import EventDTO from '../../models/event';
import EventBannerImage from '../atoms/EventBannerImage/EventBannerImage';
import {
  AR_TIMEZONE, SearchGroup, SUPPORT_WHATSAPP_URL, eventPlaceLabel, highlightParts,
} from '../../utils/eventSearch';

const FALLBACK_BANNER = '/favicon.png';

type Variant = 'desktop' | 'mobile';

export const formatEventWhen = (e: EventDTO): string => {
  const dt = DateTime.fromISO(e.date, { zone: AR_TIMEZONE }).setLocale('es');
  if (!dt.isValid) { return ''; }
  const label = dt.toFormat("ccc d LLL · HH:mm 'hs'").replace(/\./g, '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

interface SectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

const SectionLabel = ({ children, className = '' }: SectionLabelProps) => (
  <p className={`text-sm-semibold text-text-secondary m-0 ${className}`}>{children}</p>
);

interface HighlightedProps {
  text: string;
  query: string;
}

const Highlighted = ({ text, query }: HighlightedProps) => (
  <>
    {highlightParts(text, query).map((part, i) => (part.hit ? (
      <mark key={i} className="bg-primary-500/20 text-inherit rounded px-px">{part.text}</mark>
    ) : (
      <React.Fragment key={i}>{part.text}</React.Fragment>
    )))}
  </>
);

interface EventRowProps {
  event: EventDTO;
  query: string;
  variant: Variant;
  highlighted?: boolean;
  onSelect: (e: EventDTO) => void;
  onHover?: () => void;
}

export const EventRow = ({
  event, query, variant, highlighted, onSelect, onHover,
}: EventRowProps) => {
  const isDesktop = variant === 'desktop';
  const category = event.categories?.[0];
  return (
    <button
      type="button"
      onClick={() => onSelect(event)}
      onMouseEnter={onHover}
      className={`flex items-center w-full text-left rounded-2xl transition-colors ${
        isDesktop ? 'gap-4 p-2.5' : 'gap-3 py-1.5'
      } ${highlighted ? 'bg-surface-secondary' : 'hover:bg-surface-secondary'}`}
    >
      <span className={`relative flex-shrink-0 overflow-hidden rounded-xl bg-surface-secondary ${
        isDesktop ? 'w-24 h-[60px]' : 'w-[72px] h-[72px]'
      }`}
      >
        <EventBannerImage
          src={event.banner_image || FALLBACK_BANNER}
          alt=""
          sizes="96px"
          loading="lazy"
          className="object-cover"
        />
      </span>
      <span className="flex flex-col gap-1 min-w-0 flex-1">
        <span className={`text-md-semibold text-text-primary leading-snug ${isDesktop ? 'truncate' : 'line-clamp-2'}`}>
          <Highlighted text={event.name} query={query} />
        </span>
        <span className="text-sm-regular text-text-secondary truncate">
          {formatEventWhen(event)}
          {isDesktop && eventPlaceLabel(event) && (
            <>
              {' · '}
              <Highlighted text={eventPlaceLabel(event)} query={query} />
            </>
          )}
        </span>
        {!isDesktop && eventPlaceLabel(event) && (
          <span className="text-sm-regular text-text-secondary truncate">
            <Highlighted text={eventPlaceLabel(event)} query={query} />
          </span>
        )}
      </span>
      {event.is_sold_out && (
        <span className="flex-shrink-0 tag-warning text-xs-semibold">Agotado</span>
      )}
      {isDesktop && !event.is_sold_out && category && (
        <span className="flex-shrink-0 px-3 py-1 rounded-full bg-surface-secondary text-text-primary text-xs-semibold">
          {category.name}
        </span>
      )}
    </button>
  );
};

interface GroupRowProps {
  group: SearchGroup;
  kind: 'venue' | 'producer';
  onPick: (name: string) => void;
}

const GroupRow = ({ group, kind, onPick }: GroupRowProps) => (
  <button
    type="button"
    onClick={() => onPick(group.name)}
    className="flex items-center gap-3 w-full text-left rounded-2xl p-2 hover:bg-surface-secondary transition-colors"
  >
    {kind === 'venue' ? (
      <span className="w-10 h-10 rounded-full bg-primary-500/10 inline-flex items-center justify-center flex-shrink-0">
        <PiMapPin className="text-lg text-primary-500" aria-hidden="true" />
      </span>
    ) : (
      <span className="w-10 h-10 rounded-full bg-text-primary text-surface-base text-md-bold inline-flex items-center justify-center flex-shrink-0">
        {group.name.charAt(0).toUpperCase()}
      </span>
    )}
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-md-semibold text-text-primary truncate">{group.name}</span>
      <span className="text-sm-regular text-text-secondary">{group.sub}</span>
    </span>
  </button>
);

interface FeaturedListProps {
  title: string;
  events: EventDTO[];
  variant: Variant;
  onSelect: (e: EventDTO) => void;
}

const FeaturedList = ({
  title, events, variant, onSelect,
}: FeaturedListProps) => (
  <div className="flex flex-col gap-1">
    <SectionLabel className={variant === 'desktop' ? 'px-2 pt-2 pb-1.5' : 'pb-1.5'}>{title}</SectionLabel>
    {events.map((e) => (
      <EventRow key={e.id} event={e} query="" variant={variant} onSelect={onSelect} />
    ))}
  </div>
);

export interface SearchNotice {
  text: string;
  actionLabel: string;
  onAction: () => void;
}

export interface SearchResultsProps {
  variant: Variant;
  notice?: SearchNotice;
  active: boolean;
  query: string;
  eventsTitle: string;
  results: EventDTO[];
  maxEvents: number;
  venues: SearchGroup[];
  producers: SearchGroup[];
  featured: EventDTO[];
  activeIndex?: number;
  onHoverIndex?: (i: number) => void;
  onSelectEvent: (e: EventDTO) => void;
  onPickGroup: (name: string) => void;
  onShowAll: () => void;
  onClear: () => void;
}

const SearchResults = ({
  variant, notice, active, query, eventsTitle, results, maxEvents, venues, producers, featured,
  activeIndex = -1, onHoverIndex, onSelectEvent, onPickGroup, onShowAll, onClear,
}: SearchResultsProps) => {
  const isDesktop = variant === 'desktop';
  const shown = results.slice(0, maxEvents);
  const hasAny = results.length + venues.length + producers.length > 0;

  // Nothing typed yet: what's coming up.
  if (!active) {
    return (
      <div className={isDesktop ? 'p-4' : 'px-4 py-5'}>
        <FeaturedList title="Próximos destacados" events={featured} variant={variant} onSelect={onSelectEvent} />
      </div>
    );
  }

  if (!hasAny) {
    const emptyBlock = (
      <div className={`flex flex-col gap-3 ${isDesktop ? 'p-8' : 'items-center text-center px-6 pt-8 pb-4'}`}>
        <span className="w-12 h-12 rounded-full bg-surface-secondary inline-flex items-center justify-center">
          <PiMagnifyingGlass className="text-2xl text-text-secondary" aria-hidden="true" />
        </span>
        <p className="text-xl-semibold text-text-primary m-0">
          {query.trim() ? `No encontramos eventos para “${query.trim()}”` : 'No hay eventos para ese filtro'}
        </p>
        <p className="text-md-regular text-text-secondary m-0">
          Probá con el nombre del artista, del lugar o de la productora. Si el evento todavía no salió a la venta, puede que no aparezca.
        </p>
        <div className={`flex gap-3 mt-2 ${isDesktop ? '' : 'flex-col w-full'}`}>
          {isDesktop && (
            <button
              type="button"
              onClick={onClear}
              className="h-11 px-5 rounded-full border border-border-secondary bg-surface-base text-md-semibold text-text-primary shadow-sm"
            >
              Borrar búsqueda
            </button>
          )}
          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="h-11 px-5 rounded-full border border-primary-200 text-md-semibold text-text-primary no-underline inline-flex items-center justify-center gap-2"
          >
            <PiWhatsappLogo className="text-lg text-primary-500" aria-hidden="true" />
            Pedir ayuda por WhatsApp
          </a>
        </div>
      </div>
    );
    return isDesktop ? (
      <div className="grid grid-cols-2">
        <div className="border-r border-border-secondary">{emptyBlock}</div>
        <div className="p-4">
          <FeaturedList title="Mientras tanto, lo que viene" events={featured} variant={variant} onSelect={onSelectEvent} />
        </div>
      </div>
    ) : (
      <div>
        {emptyBlock}
        <div className="px-4 pb-6">
          <FeaturedList title="Mientras tanto, lo que viene" events={featured} variant={variant} onSelect={onSelectEvent} />
        </div>
      </div>
    );
  }

  const noticeBlock = notice && (
    <div className={`flex items-center justify-between gap-3 rounded-2xl bg-warning-100/60 text-text-primary ${
      isDesktop ? 'mx-4 mt-4 px-4 py-3' : 'mb-3 px-4 py-3 flex-wrap'
    }`}
    >
      <span className="text-sm-medium">{notice.text}</span>
      <button
        type="button"
        onClick={notice.onAction}
        className="flex-shrink-0 text-sm-semibold text-primary-500 hover:text-primary-700 underline-offset-2 hover:underline"
      >
        {notice.actionLabel}
      </button>
    </div>
  );

  const eventsColumn = (
    <div className="flex flex-col gap-1">
      <SectionLabel className={isDesktop ? 'px-2 pt-2 pb-1.5' : 'pb-1.5'}>{eventsTitle}</SectionLabel>
      {shown.length === 0 && (
        <p className={`text-sm-regular text-text-secondary m-0 ${isDesktop ? 'px-2 pb-2' : 'pb-2'}`}>
          Ningún evento con ese nombre. Mirá los lugares y productoras.
        </p>
      )}
      {shown.map((e, i) => (
        <EventRow
          key={e.id}
          event={e}
          query={query}
          variant={variant}
          highlighted={i === activeIndex}
          onHover={onHoverIndex ? () => onHoverIndex(i) : undefined}
          onSelect={onSelectEvent}
        />
      ))}
    </div>
  );

  const groups = (
    <>
      {(isDesktop || venues.length > 0) && (
        <div className="flex flex-col gap-1">
          <SectionLabel className={isDesktop ? 'px-2 pt-2 pb-1.5' : 'pt-4 pb-1.5'}>Lugares</SectionLabel>
          {venues.length === 0 && <p className="text-sm-regular text-text-tertiary m-0 px-2 pb-2">Sin coincidencias</p>}
          {venues.map((g) => <GroupRow key={g.name} group={g} kind="venue" onPick={onPickGroup} />)}
        </div>
      )}
      {(isDesktop || producers.length > 0) && (
        <div className="flex flex-col gap-1">
          <SectionLabel className={isDesktop ? 'px-2 pt-4 pb-1.5' : 'pt-4 pb-1.5'}>Productoras</SectionLabel>
          {producers.length === 0 && <p className="text-sm-regular text-text-tertiary m-0 px-2">Sin coincidencias</p>}
          {producers.map((g) => <GroupRow key={g.name} group={g} kind="producer" onPick={onPickGroup} />)}
        </div>
      )}
    </>
  );

  if (!isDesktop) {
    return (
      <div className="px-4 py-4 flex flex-col">
        {noticeBlock}
        {eventsColumn}
        {groups}
      </div>
    );
  }

  return (
    <>
      {noticeBlock}
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="p-4 border-r border-border-secondary">{eventsColumn}</div>
        <div className="p-4 bg-surface-secondary/40">{groups}</div>
      </div>
      <div className="h-14 px-6 border-t border-border-secondary flex items-center justify-between">
        {results.length > 0 ? (
          <button type="button" onClick={onShowAll} className="inline-flex items-center gap-1.5 text-md-bold text-primary-500 hover:text-primary-700">
            {results.length === 1 ? 'Ver el resultado' : `Ver los ${results.length} resultados`}
            <PiArrowRight aria-hidden="true" />
          </button>
        ) : <span />}
        <span className="text-xs-medium text-text-tertiary">↑ ↓ para elegir · Enter para ver todos · Esc para cerrar</span>
      </div>
    </>
  );
};

export default SearchResults;
